import { Box, Paper, Rating, Button } from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { setInPageLoading, setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";

import { allEmployeeSkiillResponses, viewActiveUsers, exportAllSkillResponsesCSV } from "../../Service/Apis";

import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridPagination,
} from "@mui/x-data-grid";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import Loading from "../../components/Loading/Loading";
import { AllEmployeeResponsesType, EmployeeSkillSetType } from "./Types/SkillTypes";
import { PAGINATION_CONFIG, getPageSizeOptionsForDataGrid } from "utils/paginationConfig";

// Custom Toolbar with Search and Pagination at the top
interface CustomToolbarProps {
  handleExportSummary: () => void;
  isExportingSummary: boolean;
}

function CustomToolbarWithPagination({ handleExportSummary, isExportingSummary }: CustomToolbarProps) {
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.5rem",
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <Button
          size="small"
          onClick={handleExportSummary}
          disabled={isExportingSummary}
          startIcon={<FileDownloadIcon />}
          sx={{
            textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 500,
            minWidth: 'auto',
            px: 1,
            color: 'success.main',
            '&:hover': {
              backgroundColor: 'rgba(46, 125, 50, 0.04)',
            },
          }}
        >
          {isExportingSummary ? 'Exporting...' : 'Export Summary'}
        </Button>
        <GridToolbarQuickFilter sx={{ ml: 2 }} />
      </Box>
      <GridPagination />
    </GridToolbarContainer>
  );
}

interface Employeetype {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  gender: string;
  roles: [];
  designation: string;
  date_of_birth: string;
  date_of_joining: string;
  date_of_confirmation: string;
  date_of_exit: string;
  email: string;
  password: string;
}

const DownloadSkillsReview = () => {
  const [employeesData, setEmployeesData] = useState<Employeetype[]>([]);
  const [employee, setEmployee] = useState<Employeetype | null>(null);

  const [data, setData] = useState<AllEmployeeResponsesType[]>([]);
  const [selectedSkill, setSelectedSkill] =
    useState<EmployeeSkillSetType | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE });
  const [rowCount, setRowCount] = useState<number>(0);
  
  // State for exporting
  const [isExportingSummary, setIsExportingSummary] = useState(false);

  // Redux Hooks
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isLoading = useSelector((state: any) => state.loading.inPageLoading);

  const handleEmployeeChange = (e: any, values: any) => {
    setEmployee(values);
  };

  // Function to export summary report directly from backend (Employee → Skills pivot)
  const handleExportSummary = async () => {
    try {
      setIsExportingSummary(true);
      
      // Get authentication token
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error("No authentication token found");
        setIsExportingSummary(false);
        return;
      }
      
      // Use axios with responseType: "blob" for file download (consistent with codebase)
      const response = await axios.get(exportAllSkillResponsesCSV, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'skills-by-employee-complete-matrix.csv';
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setIsExportingSummary(false);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setIsExportingSummary(false);
    }
  };

  const rows = useMemo(() => {
    return data.map((question, index) => ({
      ...question,
      questionIndex: index + 1,
    }));
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "questionIndex",
      headerName: "Sr. No",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Sr No.
        </strong>
      ),
      //   flex: 1,
      width: 80,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
    },
    {
      field: "employee_name",
      headerName: "Employee Name",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ paddingRight: 2, fontSize: 17, textAlign: "center" }}
        >
          Employee Name
        </strong>
      ),
      //   flex: 1,
      width: 160,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
    },
    {
      field: "skill_name",
      headerName: "Skill Name",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Skill Name
        </strong>
      ),
      width: 240,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderCell: (params: any) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3, textAlign: "center", width: "100%" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "question",
      headerName: " Question",
      flex: 1,
      minWidth: 600,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Question
        </strong>
      ),
      renderCell: (params: any) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3, pr: 1, textAlign: "center", width: "100%" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "employee_skill_response",
      headerName: " Employee Response",
      width: 185,
      //   flex: 1,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Employee Response
        </strong>
      ),
      renderCell: (params: any) => (
        <Rating
          readOnly
          className="rating-color"
          value={parseInt(
            params.row.employee_skill_response
              ? params.row.employee_skill_response
              : "0"
          )}
        />
      ),
    },
    {
      field: "manager_skill_response",
      headerName: " Manager Response",
      width: 185,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Manager Response
        </strong>
      ),
      renderCell: (params: any) => (
        <Rating
          readOnly
          className="rating-color"
          value={parseInt(
            params.row.manager_skill_response
              ? params.row.manager_skill_response
              : "0"
          )}
        />
      ),
    },
  ];

  useEffect(() => {
    // API Call get the User Details.
    const fetchUserDetails = async () => {
      try {
        dispatch(setIsLoading(true));
        const response = await dispatch2(fetchData(`${viewActiveUsers}`));
        setEmployeesData(response as Employeetype[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchUserDetails();
  }, []);

  useEffect(() => {
    // API call to get the employee skill responses with pagination.
    const fetchEmployeesSkillResponse = async () => {
      try {
        dispatch(setInPageLoading(true));
        const params = {
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
        } as any;
        const response = await dispatch2(
          fetchData(`${allEmployeeSkiillResponses}`, params)
        );
        const results = (response && (response as any).results)
          ? (response as any).results
          : (Array.isArray(response) ? response : []);
        const count = (response && typeof (response as any).count === 'number')
          ? (response as any).count
          : (Array.isArray(response) ? (response as any).length : 0);
        setData(results as AllEmployeeResponsesType[]);
        setRowCount(count);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };

    fetchEmployeesSkillResponse();
  }, [paginationModel]);

  // Loading Spinner Component
  if (isLoading) {
    return (
      <>
        <Loading isLoading={isLoading as boolean} />
      </>
    );
  }

  return (
    <>
      <Box id="main-component" sx={{ mt: 2, mb: 10 }}>
        <Box sx={{ width: "98%", display: "flex", mx: 1, mt: 2 }}>
          <Box sx={{ width: "100%" }}>
            {data.length === 0 ? (
              <>
                <Box
                  sx={{
                    mt: 12,
                    left: "50%",
                    // transform: "translate(-50%, -50%)",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <NothingToDisplayComponent />
                </Box>
              </>
            ) : (
              <>
                {/* {data.map((emp, index) => {
                  return ( */}
                <Box sx={{ width: "100%" }}>
                  <Paper sx={{ width: "100%", m: 1, p: 2 }}>
                    <DataGrid
                      autoHeight
                      rowHeight={64}
                      getRowHeight={() => 'auto'}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
                          },
                        },
                      }}
                      disableColumnMenu
                      sx={{
                        width: "100%",
                        height: "100%",
                        "--DataGrid-overlayHeight": "50vh",
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#e8f5e9',
                        },
                        '& .MuiDataGrid-columnHeader': {
                          backgroundColor: '#e8f5e9',
                          color: '#1b5e20',
                        },
                        '& .MuiDataGrid-columnSeparator': {
                          color: '#1b5e20',
                          borderColor: '#e0eee3',
                        },
                      }}
                      pagination
                      paginationMode="server"
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={getPageSizeOptionsForDataGrid()}
                      rows={rows}
                      columns={columns}
                      //   disableColumnFilter
                      disableColumnSelector
                      //   disableDensitySelector
                      slots={{
                        toolbar: CustomToolbarWithPagination,
                        noRowsOverlay: CustomNoRowsOverlay,
                        noResultsOverlay: CustomNoRowsOverlay,
                      }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          style: { margin: "0.5rem" },
                          handleExportSummary: handleExportSummary,
                          isExportingSummary: isExportingSummary,
                        },
                      }}
                      getRowId={(row) => `${row.user_id}-${row.skill_question_id}`}
                    />
                  </Paper>
                </Box>
                {/* ); */}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DownloadSkillsReview;
